import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthService } from '../../modules/auth/auth.service';
import {
  MOCK_BRANCHES,
  MOCK_CATEGORIES,
  MOCK_LOCATIONS,
  MOCK_SUPPLIERS,
  MOCK_UNITS,
  MOCK_USERS,
} from '../data/mock-settings.data';
import { GenericMultiResultDto } from '../models/api.models';
import {
  AppUser,
  AppRole,
  Branch,
  Category,
  Location,
  Supplier,
  UnitOfMeasure,
} from '../models/settings.models';
import {
  findResultSetWithColumn,
  firstResultSet,
  isActiveRow,
  pickString,
  procWriteResult,
} from '../utils/api-shape.util';
import { scopeBranches, scopeLocations } from '../utils/branch-scope.util';
import { ApiService } from './api.service';

export type SettingsWriteResult = { success: boolean; message: string };

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  private readonly branches = signal<Branch[]>(
    environment.useMockData ? [...MOCK_BRANCHES] : [],
  );
  private readonly locations = signal<Location[]>(
    environment.useMockData ? [...MOCK_LOCATIONS] : [],
  );
  private readonly categories = signal<Category[]>(
    environment.useMockData ? [...MOCK_CATEGORIES] : [],
  );
  private readonly units = signal<UnitOfMeasure[]>(
    environment.useMockData ? [...MOCK_UNITS] : [],
  );
  private readonly suppliers = signal<Supplier[]>(
    environment.useMockData ? [...MOCK_SUPPLIERS] : [],
  );
  private readonly users = signal<AppUser[]>(
    environment.useMockData ? [...MOCK_USERS] : [],
  );
  private readonly roles = signal<AppRole[]>([]);
  private readonly loadError = signal<string | null>(null);

  readonly allBranches = this.branches.asReadonly();
  readonly allLocations = this.locations.asReadonly();
  readonly scopedBranches = computed(() =>
    scopeBranches(this.auth, this.branches()),
  );
  readonly scopedLocations = computed(() =>
    scopeLocations(this.auth, this.locations()),
  );
  /** All active branches in the signed-in user's company (for stock transfer destinations). */
  readonly companyBranches = computed(() => this.branches());
  /** All store locations at company branches. */
  readonly companyLocations = computed(() => this.locations());
  readonly allCategories = this.categories.asReadonly();
  readonly allUnits = this.units.asReadonly();
  readonly allSuppliers = this.suppliers.asReadonly();
  readonly allUsers = this.users.asReadonly();
  readonly allRoles = this.roles.asReadonly();
  readonly lookupLoadError = this.loadError.asReadonly();

  constructor() {
    if (!environment.useMockData && sessionStorage.getItem('ims-auth')) {
      this.refreshFromApi();
    }
  }

  refreshFromApi(): void {
    if (environment.useMockData) {
      return;
    }

    this.loadError.set(null);

    this.api
      .get<GenericMultiResultDto>('/Branches', { includeAll: true })
      .pipe(
        tap((payload) => this.applyBranches(payload)),
        catchError((error) => {
          this.noteLookupError('Branches', error);
          return of(null);
        }),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/StoreLocations')
      .pipe(
        tap((payload) => this.applyLocations(payload)),
        catchError((error) => {
          this.noteLookupError('Store locations', error);
          return of(null);
        }),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/ItemCategories')
      .pipe(
        tap((payload) => this.applyCategories(payload)),
        catchError((error) => {
          this.noteLookupError('Categories', error);
          return of(null);
        }),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/Suppliers')
      .pipe(
        tap((payload) => this.applySuppliers(payload)),
        catchError((error) => {
          this.noteLookupError('Suppliers', error);
          return of(null);
        }),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/UnitsOfMeasure')
      .pipe(
        tap((payload) => this.applyUnits(payload)),
        catchError((error) => {
          this.noteLookupError('Units', error);
          return of(null);
        }),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/Users')
      .pipe(
        tap((payload) => this.applyUsers(payload)),
        catchError((error) => {
          this.noteLookupError('Users', error);
          return of(null);
        }),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/Users/roles')
      .pipe(
        tap((payload) => this.applyRoles(payload)),
        catchError((error) => {
          this.noteLookupError('Roles', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Fallback when /Branches or /StoreLocations fail: fill dropdowns from
   * other Gateway payloads that already include lookup result sets.
   */
  hydrateLookupsFromPayload(payload: unknown): void {
    if (environment.useMockData || !payload) {
      return;
    }

    if (!this.branches().length) {
      this.applyBranches(payload as GenericMultiResultDto);
    }
    if (!this.locations().length) {
      this.applyLocations(payload as GenericMultiResultDto);
    }
  }

  getBranches(): Branch[] {
    return this.branches();
  }

  getLocations(branchId?: string): Location[] {
    const list = scopeLocations(this.auth, this.locations());
    return branchId
      ? list.filter((location) => location.branchId === branchId)
      : list;
  }

  getCompanyLocations(branchId?: string): Location[] {
    const list = this.locations();
    return branchId
      ? list.filter((location) => location.branchId === branchId)
      : list;
  }

  getCategories(): Category[] {
    return this.categories();
  }

  getUnits(): UnitOfMeasure[] {
    return this.units();
  }

  getSuppliers(): Supplier[] {
    return this.suppliers();
  }

  getUsers(): AppUser[] {
    return this.users();
  }

  addCategory(category: Omit<Category, 'id'>): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.categories.update((list) => [
        ...list,
        { ...category, id: crypto.randomUUID() },
      ]);
      return of({ success: true, message: 'Category added successfully.' });
    }

    if (!category.name?.trim()) {
      return of({ success: false, message: 'Category name is required.' });
    }

    return this.api
      .post<GenericMultiResultDto>('/ItemCategories', {
        categoryName: category.name.trim(),
        categoryCode: category.code?.trim() || null,
        parentCategoryID: null,
        isActive: category.status === 'active',
      })
      .pipe(this.writeOp('Category added successfully.', 'Failed to add category.'));
  }

  addBranch(branch: Omit<Branch, 'id'>): Observable<{ success: boolean; message: string }> {
    if (environment.useMockData) {
      this.branches.update((list) => [
        ...list,
        { ...branch, id: crypto.randomUUID() },
      ]);
      return of({ success: true, message: 'Branch added successfully.' });
    }

    return this.api
      .post<GenericMultiResultDto>('/Branches', {
        branchName: branch.name,
        branchCode: branch.code || null,
        province: null,
        city: branch.city || null,
        addressLine1: null,
        isActive: branch.status === 'active',
      })
      .pipe(
        map((payload) => {
          const result = procWriteResult(payload);
          if (result.success) {
            this.refreshFromApi();
          }
          return {
            success: result.success,
            message: result.success
              ? 'Branch added successfully.'
              : result.message || 'Failed to add branch.',
          };
        }),
        catchError((error) =>
          of({
            success: false,
            message: this.writeErrorMessage(error, 'Failed to add branch.'),
          }),
        ),
      );
  }

  addLocation(location: Omit<Location, 'id'>): Location {
    const created: Location = { ...location, id: crypto.randomUUID() };

    if (environment.useMockData) {
      this.locations.update((list) => [...list, created]);
      return created;
    }

    this.api
      .post('/StoreLocations', {
        branchID: Number(location.branchId) || 0,
        locationName: location.name,
        locationType: location.type,
        isActive: location.status === 'active',
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        catchError(() => of(null)),
      )
      .subscribe();

    return created;
  }

  addUnit(unit: Omit<UnitOfMeasure, 'id'>): UnitOfMeasure {
    const created: UnitOfMeasure = { ...unit, id: crypto.randomUUID() };

    if (environment.useMockData) {
      this.units.update((list) => [...list, created]);
      return created;
    }

    this.api
      .post('/UnitsOfMeasure', {
        unitName: unit.name,
        isActive: unit.status === 'active',
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        catchError(() => of(null)),
      )
      .subscribe();

    return created;
  }

  addUser(user: Omit<AppUser, 'id'>): Observable<{ success: boolean; message: string }> {
    if (environment.useMockData) {
      this.users.update((list) => [
        ...list,
        { ...user, id: crypto.randomUUID() },
      ]);
      return of({ success: true, message: 'User added successfully.' });
    }

    const { fullName, surname } = splitDisplayName(user.name);
    const roleId = Number(user.role);
    const password = (user.password ?? '').trim();

    if (!password) {
      return of({ success: false, message: 'Password is required.' });
    }

    if (!user.branch) {
      return of({ success: false, message: 'Branch is required.' });
    }

    return this.api
      .post<GenericMultiResultDto>('/Users', {
        emailAddress: user.email.trim().toLowerCase(),
        password,
        fullName,
        surname,
        branchID: Number(user.branch) || 0,
        roleID: Number.isFinite(roleId) && roleId > 0 ? roleId : null,
        roleName: Number.isFinite(roleId) && roleId > 0 ? null : user.role || null,
      })
      .pipe(
        map((payload) => {
          const result = procWriteResult(payload);
          if (result.success) {
            this.refreshFromApi();
          }
          return {
            success: result.success,
            message: result.success
              ? 'User added successfully.'
              : result.message || 'Failed to add user.',
          };
        }),
        catchError((error) =>
          of({
            success: false,
            message: this.writeErrorMessage(error, 'Failed to add user.'),
          }),
        ),
      );
  }

  addSupplier(supplier: Omit<Supplier, 'id'>): Supplier {
    const created: Supplier = { ...supplier, id: crypto.randomUUID() };

    if (environment.useMockData) {
      this.suppliers.update((list) => [...list, created]);
      return created;
    }

    this.api
      .post('/Suppliers', {
        supplierName: supplier.name,
        contactPerson: supplier.contactPerson,
        phoneNumber: supplier.phone,
        emailAddress: supplier.email,
        vatNumber: null,
        isActive: supplier.status === 'active',
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        catchError(() => of(null)),
      )
      .subscribe();

    return created;
  }

  updateBranch(
    id: string,
    patch: Partial<Branch>,
  ): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.branches.update((list) =>
        list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return of({ success: true, message: 'Branch updated successfully.' });
    }

    const name = (patch.name ?? '').trim();
    if (!name) {
      return of({ success: false, message: 'Branch name is required.' });
    }

    return this.api
      .put<GenericMultiResultDto>(`/Branches/${id}`, {
        branchName: name,
        branchCode: patch.code || null,
        province: null,
        city: patch.city || null,
        addressLine1: null,
        isActive: (patch.status ?? 'active') === 'active',
      })
      .pipe(this.writeOp('Branch updated successfully.', 'Failed to update branch.'));
  }

  deleteBranch(id: string): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.branches.update((list) => list.filter((row) => row.id !== id));
      return of({ success: true, message: 'Branch deleted successfully.' });
    }
    return this.api
      .delete<GenericMultiResultDto>(`/Branches/${id}`)
      .pipe(this.writeOp('Branch deleted successfully.', 'Failed to delete branch.'));
  }

  updateLocation(
    id: string,
    patch: Partial<Location>,
  ): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.locations.update((list) =>
        list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return of({ success: true, message: 'Location updated successfully.' });
    }

    const name = (patch.name ?? '').trim();
    const branchId = Number(patch.branchId);
    if (!name) {
      return of({ success: false, message: 'Location name is required.' });
    }
    if (!branchId) {
      return of({ success: false, message: 'Branch is required.' });
    }

    return this.api
      .put<GenericMultiResultDto>(`/StoreLocations/${id}`, {
        branchID: branchId,
        locationName: name,
        locationType: patch.type || 'warehouse',
        isActive: (patch.status ?? 'active') === 'active',
      })
      .pipe(
        this.writeOp('Location updated successfully.', 'Failed to update location.'),
      );
  }

  deleteLocation(id: string): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.locations.update((list) => list.filter((row) => row.id !== id));
      return of({ success: true, message: 'Location deleted successfully.' });
    }
    return this.api
      .delete<GenericMultiResultDto>(`/StoreLocations/${id}`)
      .pipe(
        this.writeOp(
          'Location deleted successfully.',
          'Failed to delete location.',
        ),
      );
  }

  updateCategory(
    id: string,
    patch: Partial<Category>,
  ): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.categories.update((list) =>
        list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return of({ success: true, message: 'Category updated successfully.' });
    }

    const name = (patch.name ?? '').trim();
    if (!name) {
      return of({ success: false, message: 'Category name is required.' });
    }

    return this.api
      .put<GenericMultiResultDto>(`/ItemCategories/${id}`, {
        categoryName: name,
        categoryCode: patch.code?.trim() || null,
        parentCategoryID: null,
        isActive: (patch.status ?? 'active') === 'active',
      })
      .pipe(
        this.writeOp('Category updated successfully.', 'Failed to update category.'),
      );
  }

  deleteCategory(id: string): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.categories.update((list) => list.filter((row) => row.id !== id));
      return of({ success: true, message: 'Category deleted successfully.' });
    }
    return this.api
      .delete<GenericMultiResultDto>(`/ItemCategories/${id}`)
      .pipe(
        this.writeOp(
          'Category deleted successfully.',
          'Failed to delete category.',
        ),
      );
  }

  updateUnit(
    id: string,
    patch: Partial<UnitOfMeasure>,
  ): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.units.update((list) =>
        list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return of({ success: true, message: 'Unit updated successfully.' });
    }

    const name = (patch.name ?? '').trim();
    if (!name) {
      return of({ success: false, message: 'Unit name is required.' });
    }

    return this.api
      .put<GenericMultiResultDto>(`/UnitsOfMeasure/${id}`, {
        unitName: name,
        isActive: (patch.status ?? 'active') === 'active',
      })
      .pipe(this.writeOp('Unit updated successfully.', 'Failed to update unit.'));
  }

  deleteUnit(id: string): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.units.update((list) => list.filter((row) => row.id !== id));
      return of({ success: true, message: 'Unit deleted successfully.' });
    }
    return this.api
      .delete<GenericMultiResultDto>(`/UnitsOfMeasure/${id}`)
      .pipe(this.writeOp('Unit deleted successfully.', 'Failed to delete unit.'));
  }

  updateSupplier(
    id: string,
    patch: Partial<Supplier>,
  ): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.suppliers.update((list) =>
        list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return of({ success: true, message: 'Supplier updated successfully.' });
    }

    const name = (patch.name ?? '').trim();
    if (!name) {
      return of({ success: false, message: 'Supplier name is required.' });
    }

    return this.api
      .put<GenericMultiResultDto>(`/Suppliers/${id}`, {
        supplierName: name,
        contactPerson: patch.contactPerson || null,
        phoneNumber: patch.phone || null,
        emailAddress: patch.email || null,
        vatNumber: null,
        isActive: (patch.status ?? 'active') === 'active',
      })
      .pipe(
        this.writeOp('Supplier updated successfully.', 'Failed to update supplier.'),
      );
  }

  deleteSupplier(id: string): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.suppliers.update((list) => list.filter((row) => row.id !== id));
      return of({ success: true, message: 'Supplier deleted successfully.' });
    }
    return this.api
      .delete<GenericMultiResultDto>(`/Suppliers/${id}`)
      .pipe(
        this.writeOp(
          'Supplier deleted successfully.',
          'Failed to delete supplier.',
        ),
      );
  }

  updateUser(
    id: string,
    patch: Partial<AppUser>,
  ): Observable<{ success: boolean; message: string }> {
    if (environment.useMockData) {
      this.users.update((list) =>
        list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return of({ success: true, message: 'User updated successfully.' });
    }

    const name = (patch.name ?? '').trim();
    const email = (patch.email ?? '').trim().toLowerCase();
    const { fullName, surname } = splitDisplayName(name);
    const roleId = Number(patch.role);
    const branchId = Number(patch.branch);
    const password = (patch.password ?? '').trim();

    if (!name || !email) {
      return of({ success: false, message: 'Name and email are required.' });
    }
    if (!branchId) {
      return of({ success: false, message: 'Branch is required.' });
    }
    if (!patch.role) {
      return of({ success: false, message: 'Role is required.' });
    }

    const body: Record<string, unknown> = {
      emailAddress: email,
      fullName,
      surname,
      branchID: branchId,
      roleID: Number.isFinite(roleId) && roleId > 0 ? roleId : null,
      roleName: Number.isFinite(roleId) && roleId > 0 ? null : patch.role || null,
    };
    if (password) {
      body['password'] = password;
    }

    const userId = Number(id);
    if (!Number.isFinite(userId) || userId <= 0) {
      return of({ success: false, message: 'Invalid user id.' });
    }

    return this.api.put<GenericMultiResultDto>(`/Users/${userId}`, body).pipe(
      map((payload) => {
        const result = procWriteResult(payload);
        if (result.success) {
          this.refreshFromApi();
        }
        return {
          success: result.success,
          message: result.success
            ? 'User updated successfully.'
            : result.message || 'Failed to update user.',
        };
      }),
      catchError((error) =>
        of({
          success: false,
          message: this.writeErrorMessage(error, 'Failed to update user.'),
        }),
      ),
    );
  }

  updateRolePermissions(
    roleId: string,
    permissionCodes: string[],
  ): Observable<{ success: boolean; message: string }> {
    if (environment.useMockData) {
      this.roles.update((list) =>
        list.map((role) =>
          role.id === roleId
            ? { ...role, permissions: [...permissionCodes] }
            : role,
        ),
      );
      return of({
        success: true,
        message: 'Role permissions updated successfully.',
      });
    }

    const id = Number(roleId);
    if (!Number.isFinite(id) || id <= 0) {
      return of({ success: false, message: 'Invalid role id.' });
    }

    return this.api
      .put<GenericMultiResultDto>(`/Users/roles/${id}/permissions`, {
        permissionCodes,
      })
      .pipe(
        map((payload) => {
          const result = procWriteResult(payload);
          if (result.success) {
            this.refreshFromApi();
          }
          return {
            success: result.success,
            message: result.success
              ? 'Role permissions updated successfully.'
              : result.message || 'Failed to update role permissions.',
          };
        }),
        catchError((error) =>
          of({
            success: false,
            message: this.writeErrorMessage(
              error,
              'Failed to update role permissions.',
            ),
          }),
        ),
      );
  }

  deleteUser(id: string): Observable<SettingsWriteResult> {
    if (environment.useMockData) {
      this.users.update((list) => list.filter((row) => row.id !== id));
      return of({ success: true, message: 'User deleted successfully.' });
    }

    return this.api
      .delete<GenericMultiResultDto>(`/Users/${id}`)
      .pipe(this.writeOp('User deleted successfully.', 'Failed to delete user.'));
  }

  private applyBranches(payload: GenericMultiResultDto): void {
    const branches = this.parseBranchRows(payload);
    this.branches.set(branches);
    this.loadError.set(null);
    if (branches.length) {
      this.tryResolveUserBranchFromLookups(branches);
    }
  }

  private parseBranchRows(payload: GenericMultiResultDto): Branch[] {
    // Prefer rows that include BranchCode (dedicated lookup / by-branch grids).
    let source = findResultSetWithColumn(payload, 'BranchCode').filter(
      (row) => {
        const id = pickString(row, 'BranchID', 'branchID', 'id');
        const name = pickString(row, 'BranchName', 'branchName', 'name');
        return !!id && !!name;
      },
    );

    if (!source.length) {
      source = firstResultSet(payload).filter((row) => {
        const id = pickString(row, 'BranchID', 'branchID', 'id');
        const name = pickString(row, 'BranchName', 'branchName', 'name');
        return (
          !!id &&
          !!name &&
          !pickString(row, 'EquipmentAssetID', 'StoreLocationID')
        );
      });
    }

    if (!source.length) {
      return [];
    }

    // Deduplicate by BranchID — branch summary grids can repeat branches.
    const map = new Map<string, Branch>();
    for (const row of source) {
      const id = pickString(row, 'BranchID', 'branchID', 'id');
      const name = pickString(row, 'BranchName', 'branchName', 'name');
      if (!id || !name || map.has(id)) {
        continue;
      }
      map.set(id, {
        id,
        code: pickString(row, 'BranchCode', 'branchCode', 'code') || `BR-${id}`,
        name,
        city: pickString(row, 'City', 'city'),
        status: isActiveRow(row) ? ('active' as const) : ('inactive' as const),
      });
    }

    return [...map.values()];
  }

  /** When login did not return BranchID, infer from a single branch in lookup data. */
  private tryResolveUserBranchFromLookups(branches: Branch[]): void {
    if (this.auth.currentBranchId() > 0) {
      return;
    }

    if (branches.length === 1) {
      const id = Number(branches[0]?.id);
      if (Number.isFinite(id) && id > 0) {
        this.auth.setUserBranchId(id);
      }
    }
  }

  private applyLocations(payload: GenericMultiResultDto): void {
    const source = findResultSetWithColumn(payload, 'StoreLocationID').filter(
      (row) => {
        const id = pickString(row, 'StoreLocationID', 'storeLocationID', 'id');
        const name = pickString(row, 'LocationName', 'locationName', 'name');
        return !!id && !!name;
      },
    );
    if (!source.length) {
      this.locations.set([]);
      return;
    }

    const map = new Map<string, Location>();
    for (const row of source) {
      const id = pickString(row, 'StoreLocationID', 'storeLocationID', 'id');
      const name = pickString(row, 'LocationName', 'locationName', 'name');
      if (!id || !name || map.has(id)) {
        continue;
      }

      const typeRaw = pickString(
        row,
        'LocationType',
        'locationType',
      ).toLowerCase();
      const type =
        typeRaw === 'store' || typeRaw === 'garage' || typeRaw === 'office'
          ? typeRaw
          : ('warehouse' as const);

      map.set(id, {
        id,
        code:
          pickString(row, 'LocationCode', 'locationCode', 'code') ||
          `LOC-${id}`,
        name,
        branchId: pickString(row, 'BranchID', 'branchID'),
        type,
        status: isActiveRow(row) ? ('active' as const) : ('inactive' as const),
      });
    }

    if (map.size) {
      this.locations.set([...map.values()]);
      this.loadError.set(null);
    }
  }

  private noteLookupError(label: string, error: unknown): void {
    const detail =
      error instanceof HttpErrorResponse
        ? error.error?.error || error.error?.message || error.message
        : 'Request failed';
    const message = `${label} failed to load (${detail}).`;
    console.error(message, error);
    if (!this.branches().length || !this.locations().length) {
      this.loadError.set(message);
    }
  }

  private writeErrorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof HttpErrorResponse)) {
      return fallback;
    }

    const fromProc = procWriteResult(error.error);
    if (fromProc.message && fromProc.message !== 'Saved.') {
      return fromProc.message;
    }

    return (
      pickString(error.error, 'message', 'Message', 'error') ||
      error.message ||
      fallback
    );
  }

  private writeOp(successMessage: string, fallback: string) {
    return (source: Observable<GenericMultiResultDto>) =>
      source.pipe(
        map((payload) => {
          const result = procWriteResult(payload);
          if (result.success) {
            this.refreshFromApi();
          }
          return {
            success: result.success,
            message: result.success
              ? successMessage
              : result.message || fallback,
          } satisfies SettingsWriteResult;
        }),
        catchError((error) =>
          of({
            success: false,
            message: this.writeErrorMessage(error, fallback),
          } satisfies SettingsWriteResult),
        ),
      );
  }

  private applyCategories(payload: GenericMultiResultDto): void {
    const rows = firstResultSet(payload);
    this.categories.set(
      rows.map((row) => ({
        id: pickString(row, 'ItemCategoryID', 'itemCategoryID', 'id'),
        code: pickString(row, 'CategoryCode', 'categoryCode', 'code'),
        name: pickString(row, 'CategoryName', 'categoryName', 'name'),
        description: '',
        status: isActiveRow(row) ? ('active' as const) : ('inactive' as const),
      })).filter((row) => !!row.id && !!row.name),
    );
  }

  private applySuppliers(payload: GenericMultiResultDto): void {
    const rows = firstResultSet(payload);
    this.suppliers.set(
      rows.map((row) => ({
        id: pickString(row, 'SupplierID', 'supplierID', 'id'),
        code: pickString(row, 'SupplierCode', 'supplierCode', 'code'),
        name: pickString(row, 'SupplierName', 'supplierName', 'name'),
        contactPerson: pickString(row, 'ContactPerson', 'contactPerson'),
        email: pickString(row, 'EmailAddress', 'emailAddress', 'email'),
        phone: pickString(row, 'PhoneNumber', 'phoneNumber', 'phone'),
        status: isActiveRow(row) ? ('active' as const) : ('inactive' as const),
      })).filter((row) => !!row.id && !!row.name),
    );
  }

  private applyUnits(payload: GenericMultiResultDto): void {
    const rows = firstResultSet(payload);
    this.units.set(
      rows.map((row) => {
        const code = pickString(row, 'UnitCode', 'unitCode', 'code');
        const name = pickString(row, 'UnitName', 'unitName', 'name');
        return {
          id: pickString(row, 'UnitOfMeasureID', 'unitOfMeasureID', 'id'),
          code: code || slug(name),
          name,
          abbreviation: code || name.slice(0, 3).toUpperCase(),
          status: isActiveRow(row)
            ? ('active' as const)
            : ('inactive' as const),
        };
      }).filter((row) => !!row.id && !!row.name),
    );
  }

  private applyUsers(payload: GenericMultiResultDto): void {
    const rows = firstResultSet(payload);
    this.users.set(
      rows
        .map((row) => {
          const id = pickString(row, 'UserID', 'userID', 'id');
          const email = pickString(row, 'EmailAddress', 'emailAddress', 'email');
          const fullName = pickString(row, 'FullName', 'fullName');
          const surname = pickString(row, 'Surname', 'surname');
          const name = joinDisplayName(fullName, surname) || email;
          const roleId = pickString(row, 'RoleID', 'roleID');
          const roleName = pickString(row, 'RoleName', 'roleName');
          const codes = pickString(
            row,
            'PermissionCodes',
            'permissionCodes',
            'Permissions',
            'permissions',
          );
          return {
            id,
            name,
            email,
            role: roleId || roleName,
            branch: pickString(row, 'BranchID', 'branchID', 'branch'),
            status: 'active' as const,
            permissions: codes
              ? codes.split(',').map((code) => code.trim()).filter(Boolean)
              : [],
          } satisfies AppUser;
        })
        .filter((row) => !!row.id && !!row.email),
    );
  }

  private applyRoles(payload: GenericMultiResultDto): void {
    const rows = firstResultSet(payload);
    this.roles.set(
      rows
        .map((row) => {
          const codes = pickString(
            row,
            'PermissionCodes',
            'permissionCodes',
            'Permissions',
            'permissions',
          );
          return {
            id: pickString(row, 'RoleID', 'roleID', 'id'),
            name: pickString(row, 'RoleName', 'roleName', 'name'),
            description: pickString(row, 'Description', 'description'),
            permissions: codes
              ? codes
                  .split(',')
                  .map((code) => code.trim())
                  .filter(Boolean)
              : [],
          };
        })
        .filter((row) => !!row.id && !!row.name),
    );
  }
}

function slug(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 12);
}

function joinDisplayName(fullName: string, surname: string): string {
  const first = fullName.trim();
  const last = surname.trim();
  if (!first) {
    return last;
  }
  if (!last) {
    return first;
  }
  if (first.toLowerCase().endsWith(last.toLowerCase())) {
    return first;
  }
  return `${first} ${last}`;
}

function splitDisplayName(name: string): { fullName: string; surname: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    const value = parts[0] ?? '';
    return { fullName: value, surname: value };
  }
  return {
    fullName: parts.slice(0, -1).join(' '),
    surname: parts[parts.length - 1] ?? '',
  };
}
